import { useCallback, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import initData from 'init-data';
import { buildInitialGtmDataLayerFromInitData } from 'tradera/utils/google-tagmanager';

import { getCurrentPageFromRoute } from 'tradera/helpers/get-current-page-helper';
import { isDev } from 'tradera/legacy/static/utils/environment';
import { useOnMount } from 'tradera/hooks/use-on-mount';
import { useTimeout } from 'tradera/hooks/use-timeout';
import { useStore } from 'react-redux';

const loadWarningTimeout = 5000;

const logDebug = (message) => {
    if (isDev()) {
        console.log(
            `%c ${message} `,
            'background:green; color:white; font-size:12px;'
        );
    }
};

const logWarning = (message) => {
    if (isDev()) {
        console.log(
            `%c Warning! ${message} `,
            'background:yellow; color:black; font-size:12px;'
        );
    }
};

const UNINITIALIZED = -1;
const NOT_LOADED = 0;
const LOADING = 1;
const LOADED = 2;

export const useLoading = (routeMap) => {
    const [currentPage, setCurrentPage] = useState(null);
    const [status, setStatus] = useState(UNINITIALIZED);
    const navigationKeyRef = useRef(0);
    const [navigationKey, setNavigationKey] = useState(
        navigationKeyRef.current
    );
    const store = useStore();
    const location = useLocation();
    const history = useHistory();
    const isLoading = status === LOADING;
    const isLoaded = status === LOADED;

    const setLoading = useCallback(() => {
        if (navigationKey !== navigationKeyRef.current) {
            logWarning('Old setLoading was called');
            return;
        }
        setStatus((oldStatus) => {
            if (oldStatus !== UNINITIALIZED && oldStatus !== NOT_LOADED) {
                logWarning(`Bad state: ${oldStatus}`);
            }
            return LOADING;
        });
    }, [navigationKey]);

    const setLoaded = useCallback(() => {
        if (!currentPage) {
            logWarning('No current page yet. Cannot set loaded.');
            return;
        }
        if (navigationKey !== navigationKeyRef.current) {
            logWarning('Old setLoaded was called');
            return;
        }
        currentPage.onLoaded && currentPage.onLoaded(store.getState);
        GtmService.trackPageView();
        logDebug(`${currentPage.pageType} page is loaded`);
        setStatus((oldStatus) => {
            if (oldStatus === LOADED) {
                logWarning('Already loaded');
            }
            return LOADED;
        });
    }, [navigationKey, currentPage, store]);

    const onNavigate = useCallback(
        (location) => {
            const page = getCurrentPageFromRoute(routeMap, location);
            if (!page) {
                logWarning(`No page for ${location.pathname}`);
                return;
            }

            GtmService.newSpaPage(
                page.pageType,
                buildInitialGtmDataLayerFromInitData(initData)
            );
            // eslint-disable-next-line better-mutation/no-mutation
            setNavigationKey(++navigationKeyRef.current);
            setCurrentPage((previousPage) => {
                logDebug(`${previousPage?.pageType} -> ${page?.pageType}`);
                return page;
            });
            setStatus(NOT_LOADED);

            if (!page.mustLoadDataBeforePageTracking) {
                page.onLoaded && page.onLoaded(store.getState);
                GtmService.trackPageView();
                setStatus(() => {
                    logDebug(
                        `${page.pageType} page is loaded, data not required.`
                    );
                    return LOADED;
                });
            }
        },
        [routeMap, store]
    );

    useOnMount(() => {
        onNavigate(location);
        return history.listen(onNavigate);
    });

    // Monitor that a page comes loaded. If not we need to know
    useTimeout(
        () => {
            logWarning(
                `Page tracking may not be working, still waiting for data to load after ${
                    loadWarningTimeout / 1000
                } s. PageType: ${currentPage.pageType} path: ${
                    location.pathname
                }`
            );
        },
        isLoaded ? 0 : loadWarningTimeout,
        [isLoaded]
    );

    return {
        isLoading,
        isLoaded,
        setLoaded,
        setLoading
    };
};
